import React, {useEffect, useState} from "react";
import MapCanvas from "./MapCanvas";
import {MapInfo} from "../interfaces";
import Toggle from "../../../components/Toggle";
import {useOverlay} from "../overlay/OverlayContext";
import {usePlayers} from "../players/PlayerContext";
import Button from "../../../components/Button";
import {rpc_getMapInfo, rpc_requestMapImage} from "../rpc";
import {useSocket} from "../SocketContext";
import PlayerList from "../players/PlayerList";
import {LucideAlertCircle, LucideCircleSlash, LucideCircleSlash2, LucideLoader} from "lucide-react";
import SvgLoader from "../../../svg/SvgLoader";

export default function Map(){

    const socket = useSocket()
    const overlay = useOverlay()

    const [showMonuments, setShowMonuments] = useState(true)
    const [showPlayers, setShowPlayers] = useState(true)
    const [showPlayerNames, setShowPlayerNames] = useState(false)

    const [focusPlayer, setFocusPlayer] = useState<string>()
    const [focusUpdate, setFocusUpdate] = useState<number>(0)

    const [mapInfo, setMapInfo] = useState<MapInfo>()

    const [mapUpdateState, setMapUpdateState] = useState({loading: false, error: ""})

    const players = usePlayers()

    useEffect(() => {
        if (socket.isConnected){
            rpc_getMapInfo(socket).then(mapInfo => {
                if (mapInfo){
                    setMapInfo(mapInfo)
                }
            })
        }
    }, [socket])

    const reloadMap = async () => {
        setMapUpdateState({loading: true, error: ""})

        let result = await rpc_requestMapImage(socket)

        if (result.success){
            rpc_getMapInfo(socket).then(mapInfo => {
                if (mapInfo){
                    setMapInfo(mapInfo)
                }
            })
            setMapUpdateState({loading: false, error: ""})
        }
        else{
            setMapUpdateState({loading: false, error: `Error: ${result.message ?? "Unknown error (-1)"}`})
        }
    }

    if (!mapInfo){
        return <div className="size-full flex flex-row xl:flex-row p-4 relative">
            <div className="flex flex-col flex-1 self-center items-center justify-center h-full space-y-2">
                {/*<LucideLoader className="text-red-500" size={40} />*/}
                <SvgLoader />
                <p className="text-xl" >Loading Map ...</p>
            </div>
        </div>
    }

    if (!mapInfo.isAvailable){
        return <div className="size-full flex flex-row xl:flex-row p-4 relative">
            <div className="flex flex-col flex-1 self-center items-center justify-center h-full space-y-2">
                <LucideCircleSlash className="text-red-500" size={40} />
                <p className="text-red-500 text-xl" >Map is not available</p>
                <p>Make sure the latest version of the <a className="text-primary hover:underline" rel="noreferrer" href="https://codefling.com/plugins/rust-server-tool" target="_blank">RustServerTool plugin</a> is installed on your server.</p>
                {mapUpdateState.error && <p className="text-red-500 text-sm">{mapUpdateState.error}</p>}
            </div>
        </div>
    }

    return (
        <div className="size-full flex flex-row xl:flex-row p-4 relative">

            <div className="flex flex-col flex-grow flex-shrink me-2">
                <div className="bg-neutral-800 p-4 mb-2 rounded-md gap-4 flex flex-wrap items-center">
                    <Toggle label="Show Monuments" checked={showMonuments} onChange={() => setShowMonuments(!showMonuments)} />
                    <Toggle label="Show Players" checked={showPlayers} onChange={() => setShowPlayers(!showPlayers)} />
                    <Toggle label="Show Player Names" checked={showPlayerNames} onChange={() => setShowPlayerNames(!showPlayerNames)} />
                    <Button loading={mapUpdateState.loading} text="Reload Map (causes server lag)" className="bg-primary inline-flex leading-none" onClick={reloadMap} />
                </div>

                <MapCanvas mapImageUrl={`/api/client/map/${mapInfo.imageId}.webp`} focusOnSteamId={focusPlayer} focusUpdate={focusUpdate}
                           mapSize={mapInfo.size ?? 0} showMonuments={showMonuments} showPlayers={showPlayers} showPlayerNames={showPlayerNames}
                           monuments={mapInfo.monuments.map(x => { return { displayName: x.displayName, x: x.posX, z: x.posZ}}) ?? []}
                           players={players.online} showPlayerOverlay={(steamId) => overlay.openPlayerOverlay(steamId)}
                />
            </div>

            <div className="bg-neutral-800 p-4 rounded-md min-w-80 flex flex-col">
                <h4 className="font-bold text-lg">Online Players ({players.online.length})</h4>
                <p className="text-sm mb-2 text-neutral-400" >Click on a player to focus them on the map.</p>
                <PlayerList onPlayerClick={s => {
                    setFocusPlayer(s)
                    setFocusUpdate(u => u + 1)
                }} />
            </div>

        </div>
    )
}